import { Directive, Injector, OnDestroy } from '@angular/core';
import { Coluna, Exercicio, FormatoExportacao, FuncaoService, GlobalService, Login, Relatorio } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Assinaturas } from '../../../assinaturas/assinaturas';
import { AnexoLRFService } from '../../service/anexo-lrf.service';

@Directive()
export class DividaConsolidadaLiquidaRPPS implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: Login = new Login();
  protected unsubscribe: Subject<void> = new Subject();
  public valorDCL = 0;

  constructor(
    protected anexoServico: AnexoLRFService,
    protected mes: number,
    protected exercicio: Exercicio,
    protected injector: Injector,
    protected assinaturaControleInterno: boolean) {
    this.globalService = new GlobalService();
    this.funcaoService = new FuncaoService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public async obterDividaConsolidada(orgaos: number[]) {
    const result = await this.anexoServico.obterDCL(this.mes, this.exercicio.id, orgaos).toPromise();
    return this.conteudo(result[0]);
  }

  // tslint:disable: max-line-length
  public montarRelatorio(orgaos: number[], formato: FormatoExportacao = 'pdf') {
    this.anexoServico.obterDCLRpps(this.mes, this.exercicio.id, orgaos)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async dados => {
        if (formato === 'pdf') {
          Relatorio.imprimirPersonalizado(
            `DEMONSTRATIVO DA DÍVIDA CONSOLIDADA LÍQUIDA - REGIME DE PREVIDÊNCIA`
            , this.login.usuario.nome, this.login.usuario.sobrenome,
            this.login.orgao.nome, this.login.brasao,
            await this.conteudo(dados[0]),
            'portrait', 'DEMONSTRATIVO DA DÍVIDA CONSOLIDADA LÍQUIDA - REGIME DE PREVIDÊNCIA',
            {
              linhas: {
                hLineWidth() {
                  return 1;
                },
                vLineWidth() {
                  return 1;
                },
                hLineColor() {
                  return 'black';
                },
                paddingLeft() {
                  return 3;
                },
                paddingRight() {
                  return 3;
                }
              }
            });
        } else {
          this.funcaoService.exportar(formato, this.normalizar(dados[0]), 'DEMONSTRATIVO DA DÍVIDA CONSOLIDADA LÍQUIDA - REGIME DE PREVIDÊNCIA', this.colunas());
        }
      });
  }

  private async conteudo(dados: any[]): Promise<{}[]> {

    // monta o cabecalho
    const registros: {}[] = [
      [{
        text: `REFERÊNCIA: ` +
          this.globalService.obterDataBR().monthNames[this.mes - 1].toLocaleUpperCase() + '/' + this.exercicio.ano,
        alignment: 'center', bold: true, colSpan: 5
      }, '', '', '', ''],
      [{
        text: 'ESPECIFICAÇÃO',
        alignment: 'center',
        bold: true, fontSize: 7, rowSpan: 2, margin: 0
      }, {
        text: 'SALDO EXERCÍCIO ANTERIOR',
        alignment: 'center',
        bold: true, fontSize: 7, rowSpan: 2, margin: 0
      }, {
        text: `SALDO DO EXERCÍCIO DE ${this.exercicio.ano}`,
        alignment: 'center',
        bold: true, fontSize: 7, colSpan: 3
      }, '', ''],
      [{ text: '' }, { text: '' }, {
        text: 'Até o 1º Quadrimestre',
        alignment: 'center',
        bold: true, fontSize: 7, margin: 0
      }, {
        text: 'Até o 2º Quadrimestre',
        alignment: 'center',
        bold: true, fontSize: 7, margin: 0
      }, {
        text: 'Até o 3º Quadrimestre',
        alignment: 'center',
        bold: true, fontSize: 7, margin: 0
      }]
    ];

    const ass = new Assinaturas(this.login.orgao, this.injector);
    let assinaturas = await ass.dadosAssinatura(null, false, null, this.assinaturaControleInterno, false, false, `${this.funcaoService.ultimoDiaMes(this.mes)}/${this.funcaoService.strZero(this.mes, 2)}/${this.exercicio.ano}`);

    // monta o agrupamento do relatório
    const grupos = this.funcaoService.agrupar(dados, 'titulo',
      ['anterior', 'quadrimestre1', 'quadrimestre2', 'quadrimestre3']);
    console.log(grupos)
    let totalDivida1 = 0;
    let totalDivida2 = 0;
    let totalDivida3 = 0;
    let totalDivida4 = 0;
    let totalDeducao1 = 0;
    let totalDeducao2 = 0;
    let totalDeducao3 = 0;
    let totalDeducao4 = 0;
    let totalContrib1 = 0;
    let totalContrib2 = 0;
    let totalContrib3 = 0;
    let totalContrib4 = 0;
    for (const grupo of grupos) {
      registros.push([
        {
          text: grupo.grupo, bold: true, fontSize: 7, colSpan: 5
        },
        { text: '', alignment: 'right', bold: true, fontSize: 7 },
        { text: '', alignment: 'right', bold: true, fontSize: 7 },
        { text: '', alignment: 'right', bold: true, fontSize: 7 },
        { text: '', alignment: 'right', bold: true, fontSize: 7 },
      ]);
      for (const registro of grupo.registros) {
        registros.push([
          {
            text: '  ' + registro.nome, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(registro.anterior, 2, true), alignment: 'right', fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(registro.quadrimestre1, 2, true), alignment: 'right', fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(registro.quadrimestre2, 2, true), alignment: 'right', fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(registro.quadrimestre3, 2, true), alignment: 'right', fontSize: 7 },
        ]);
      }
      if (grupo.grupo === 'DÍVIDA CONSOLIDADA PREVIDENCIÁRIA - DC (I)') {
        registros.push([
          {
            text: 'TOTAL DÍVIDA CONSOLIDADA PREVIDENCIÁRIA - DC (I)', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['anterior'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre1'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre2'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre3'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        totalDivida1 = +grupo.totalizadores['anterior'];
        totalDivida2 = +grupo.totalizadores['quadrimestre1'];
        totalDivida3 = +grupo.totalizadores['quadrimestre2'];
        totalDivida4 = +grupo.totalizadores['quadrimestre3'];
      }
      if (grupo.grupo === 'DEDUÇÕES (II)¹') {
        for (const registro of grupo.registros) {
          if (registro.nome === '(-) Restos a Pagar Processados' || registro.nome === '(-) Depósitos Restituíves e Valores Vinculados') {
            totalDeducao1 -= +registro.anterior;
            totalDeducao2 -= +registro.quadrimestre1;
            totalDeducao3 -= +registro.quadrimestre2;
            totalDeducao4 -= +registro.quadrimestre3;
          } else {
            totalDeducao1 += +registro.anterior;
            totalDeducao2 += +registro.quadrimestre1;
            totalDeducao3 += +registro.quadrimestre2;
            totalDeducao4 += +registro.quadrimestre3;
          }
        }
        registros.push([
          {
            text: 'TOTAL DEDUÇÕES (II)', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(totalDeducao1, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDeducao2, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDeducao3, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDeducao4, 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
      }
      if (grupo.grupo === 'OUTROS VALORES NÃO INTEGRANTES DA DC') {
        registros.push([
          {
            text: 'TOTAL OBRIGAÇÕES NÃO INTEGRANTES DA DC', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['anterior'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre1'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre2'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre3'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
      }

    }

    registros.push([
      {
        text: 'DÍV. CONSOLID. LÍQUIDA (DCL)=(I - II)', bold: true, fontSize: 7
      },
      { text: this.funcaoService.convertToBrNumber(totalDivida1 - totalDeducao1, 2, true), alignment: 'right', bold: true, fontSize: 7 },
      { text: this.funcaoService.convertToBrNumber(totalDivida2 - totalDeducao2, 2, true), alignment: 'right', bold: true, fontSize: 7 },
      { text: this.funcaoService.convertToBrNumber(totalDivida3 - totalDeducao3, 2, true), alignment: 'right', bold: true, fontSize: 7 },
      { text: this.funcaoService.convertToBrNumber(totalDivida4 - totalDeducao4, 2, true), alignment: 'right', bold: true, fontSize: 7 },
    ]);

    return [{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 2,
        widths: ['*', 80, 80, 80, 80],
        body: registros
      }
    },
    {
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 0,
        widths: ['*', '*', '*'],
        body: assinaturas
      }
    }];
  }

  public normalizar(lista: any[]) {
    const listaExportar = []
    let totalDivida1 = 0;
    let totalDivida2 = 0;
    let totalDivida3 = 0;
    let totalDivida4 = 0;
    let totalDeducao1 = 0;
    let totalDeducao2 = 0;
    let totalDeducao3 = 0;
    let totalDeducao4 = 0;


    let anterior = 0;
    let quadrimestre1 = 0;
    let quadrimestre2 = 0;
    let quadrimestre3 = 0;

    const grupos = this.funcaoService.agrupar(lista, 'titulo',
      ['anterior', 'quadrimestre1', 'quadrimestre2', 'quadrimestre3']);
    for (const grupo of grupos) {
      anterior = grupo.totalizadores['anterior'];
      quadrimestre1 = grupo.totalizadores['quadrimestre1'];
      quadrimestre1 = grupo.totalizadores['quadrimestre2'];
      quadrimestre1 = grupo.totalizadores['quadrimestre3'];

      if (grupo.grupo === 'DÍVIDA CONSOLIDADA PREVIDENCIÁRIA - DC (I)') {
        let primeiroQuadrimestre = 0;
        let segundoQuadrimestre = 0;
        let terceiroQuadrimestre = 0;

        for (const i of grupo.registros) {
          primeiroQuadrimestre = i.quadrimestre1;
          segundoQuadrimestre = i.quadrimestre2;
          terceiroQuadrimestre = i.quadrimestre3;
        }
        totalDivida1 = +anterior;
        totalDivida2 = +primeiroQuadrimestre;
        totalDivida3 = +segundoQuadrimestre;
        totalDivida4 = +terceiroQuadrimestre;

        const divida_consolidada = {
          especificacao: grupo.grupo,
          saldo_exercicio_anterior: this.funcaoService.convertToBrNumber(anterior),
          primeiro_quadrimestre: this.funcaoService.convertToBrNumber(totalDivida2),
          segundo_quadrimestre: this.funcaoService.convertToBrNumber(totalDivida3),
          terceiro_quadrimestre: this.funcaoService.convertToBrNumber(totalDivida4),
        }
        listaExportar.push(divida_consolidada)

        for (const item of grupo.registros) {
          const items_divida_consolidada = {
            especificacao: item.nome,
            saldo_exercicio_anterior: this.funcaoService.convertToBrNumber(item.anterior),
            primeiro_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre1),
            segundo_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre2),
            terceiro_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre3),
          }
          listaExportar.push(items_divida_consolidada)
        }
        continue
      }

      if (grupo.grupo === 'DEDUÇÕES (II)¹') {
        for (const registro of grupo.registros) {
          if (registro.nome === '(-) Restos a Pagar Processados' || registro.nome === '(-) Depósitos Restituíves e Valores Vinculados') {
            totalDeducao1 -= +registro.anterior;
            totalDeducao2 -= +registro.quadrimestre1;
            totalDeducao3 -= +registro.quadrimestre2;
            totalDeducao4 -= +registro.quadrimestre3;
          } else {
            totalDeducao1 += +registro.anterior;
            totalDeducao2 += +registro.quadrimestre1;
            totalDeducao3 += +registro.quadrimestre2;
            totalDeducao4 += +registro.quadrimestre3;
          }
        }

        const deducoes = {
          especificacao: grupo.grupo,
          saldo_exercicio_anterior: this.funcaoService.convertToBrNumber(totalDeducao1,),
          primeiro_quadrimestre: this.funcaoService.convertToBrNumber(totalDeducao2,),
          segundo_quadrimestre: this.funcaoService.convertToBrNumber(totalDeducao3,),
          terceiro_quadrimestre: this.funcaoService.convertToBrNumber(totalDeducao4,),
        }
        listaExportar.push(deducoes)

        for (const item of grupo.registros) {
          const items_deducoes = {
            especificacao: item.nome,
            saldo_exercicio_anterior: this.funcaoService.convertToBrNumber(item.anterior),
            primeiro_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre1),
            segundo_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre2),
            terceiro_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre3),
          }
          listaExportar.push(items_deducoes)
        }
        continue
      }

      if (grupo.grupo === 'OUTROS VALORES NÃO INTEGRANTES DA DC') {
        let outros = {};
        outros['anterior'] = 0;
        outros['quadrimestre1'] = 0;
        outros['quadrimestre2'] = 0;
        outros['quadrimestre3'] = 0;
        for (const registro of grupo.registros) {
          outros['anterior'] = +outros['anterior'] + +registro['anterior'] * (registro['deducao'] ? -1 : 1);
          outros['quadrimestre1'] = +outros['quadrimestre1'] + +registro['quadrimestre1'] * (registro['deducao'] ? -1 : 1);
          outros['quadrimestre2'] = +outros['quadrimestre2'] + +registro['quadrimestre2'] * (registro['deducao'] ? -1 : 1);
          outros['quadrimestre3'] = +outros['quadrimestre3'] + +registro['quadrimestre3'] * (registro['deducao'] ? -1 : 1);
        }
        const outros_valores = {
          especificacao: grupo.grupo,
          saldo_exercicio_anterior: this.funcaoService.convertToBrNumber(outros['anterior']),
          primeiro_quadrimestre: this.funcaoService.convertToBrNumber(outros['quadrimestre1']),
          segundo_quadrimestre: this.funcaoService.convertToBrNumber(outros['quadrimestre2']),
          terceiro_quadrimestre: this.funcaoService.convertToBrNumber(outros['quadrimestre3']),
        }
        listaExportar.push(outros_valores)

        for (const item of grupo.registros) {
          const itens_outros_valores = {
            especificacao: item.nome,
            saldo_exercicio_anterior: this.funcaoService.convertToBrNumber(item.anterior),
            primeiro_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre1),
            segundo_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre2),
            terceiro_quadrimestre: this.funcaoService.convertToBrNumber(item.quadrimestre3),
          }
          listaExportar.push(itens_outros_valores)
        }
      }
    }
    return listaExportar

  }

  colunas(): Coluna[] {
    const colunasDefault: Coluna[] = [
      { titulo: 'ESPECIFICAÇÃO', coluna: 'especificacao', bold: true },
      { titulo: 'SALDO EXERCÍCIO ANTERIOR', coluna: 'saldo_exercicio_anterior' },
      { titulo: 'ATÉ O 1º QUADRIMESTRE', coluna: 'primeiro_quadrimestre' },
      { titulo: 'ATÉ O 2º QUADRIMESTRE', coluna: 'segundo_quadrimestre' },
      { titulo: 'ATÉ O 3º QUADRIMESTRE', coluna: 'terceiro_quadrimestre' },
    ];
    return colunasDefault;
  }

}
